import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  type MutationFunction,
} from '@tanstack/react-query';
import type { KyInstance, Options } from 'ky';
import { useRequestClient } from './useRequestClient';
import { apiEndpoints } from '../apiEndpoints/apiEndpoints';

type GetLPMutationFunctionOptions = {
  path: string;
  localPath?: boolean;
  kyInstance?: KyInstance;
  kyOptions?: Options;
};

function getLPMutationFunction<TData, TVariables>({
  path,
  localPath,
  kyInstance,
  kyOptions,
}: GetLPMutationFunctionOptions): MutationFunction<TData, TVariables> {
  const baseUrl = apiEndpoints.getAPIEndpoint('root');
  if (!baseUrl) {
    throw new Error('endpoint "root" is not defined');
  }
  return async (variables: TVariables): Promise<TData> => {
    if (!kyInstance) {
      throw new Error('Missing RequestClientProvider');
    }
    const url = `${localPath ? window.location.origin : baseUrl}${path}`;
    const response = await kyInstance<TData>(url, {
      ...kyOptions,
      method: 'POST',
      json: variables,
    }).json();
    return response;
  };
}

export function useLPMutation<TData, TVariables, TError = unknown>(
  pathOrMutationFn: string | MutationFunction<TData, TVariables>,
  options?: UseMutationOptions<TData, TError, TVariables, unknown>,
  kyOptions?: Options,
  localPath?: boolean,
): UseMutationResult<TData, TError, TVariables, unknown> {
  const kyInstance = useRequestClient();

  const mutationFn: MutationFunction<TData, TVariables> =
    typeof pathOrMutationFn === 'string'
      ? getLPMutationFunction<TData, TVariables>({
          path: pathOrMutationFn,
          localPath,
          kyInstance,
          kyOptions,
        })
      : pathOrMutationFn;

  return useMutation<TData, TError, TVariables, unknown>({
    mutationFn,
    ...options,
  });
}
